import { memo, type ReactNode } from 'react';
import { Edit2Icon, Trash2Icon } from 'lucide-react';
import { Button } from '@/components/Button';
import { formatDate, getUserInitials } from '@/utility/utility';
import { cn } from '@/lib/utils';
import type { CompanyTaskMessage } from '@gokrew/models/tasks';

type BubbleAccent = 'indigo' | 'violet';

const ACCENT_STYLES: Record<
  BubbleAccent,
  {
    avatarOwn: string;
    avatarOther: string;
    bubbleOwn: string;
    editHover: string;
    editBorder: string;
  }
> = {
  indigo: {
    avatarOwn: 'bg-indigo-600 text-white',
    avatarOther: 'bg-indigo-50 text-indigo-700',
    bubbleOwn: 'border-indigo-100 bg-indigo-50 shadow-xs',
    editHover: 'hover:text-indigo-500',
    editBorder: 'border-indigo-200 focus:ring-indigo-400',
  },
  violet: {
    avatarOwn: 'bg-violet-600 text-white',
    avatarOther: 'bg-violet-50 text-violet-700',
    bubbleOwn: 'border-violet-100 bg-violet-50',
    editHover: 'hover:text-violet-600',
    editBorder: 'border-violet-200 focus:ring-violet-400',
  },
};

export interface TaskMessageBubbleLabels {
  edited: string;
  save: string;
  cancel: string;
}

export interface TaskMessageBubbleEditing {
  editingMessageId: string | null;
  text: string;
  onTextChange: (text: string) => void;
  onStart: (message: CompanyTaskMessage) => void;
  onSave: (message: CompanyTaskMessage, text: string) => void;
  onCancel: () => void;
}

interface TaskMessageBubbleProps {
  message: CompanyTaskMessage;
  isOwn: boolean;
  accent: BubbleAccent;
  language: string;
  labels: TaskMessageBubbleLabels;
  editing: TaskMessageBubbleEditing;
  onDelete: (message: CompanyTaskMessage) => void;
  attachmentsSlot?: ReactNode;
  className?: string;
}

function TaskMessageBubbleBase({
  message,
  isOwn,
  accent,
  language,
  labels,
  editing,
  onDelete,
  attachmentsSlot,
  className,
}: TaskMessageBubbleProps) {
  const styles = ACCENT_STYLES[accent];
  const isEditing = editing.editingMessageId === message.id;

  return (
    <div className={cn('flex gap-3', isOwn && 'flex-row-reverse', className)}>
      <div
        className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-[10px] font-semibold ${
          isOwn ? styles.avatarOwn : styles.avatarOther
        }`}
      >
        {getUserInitials({
          firstName: message.senderUser?.firstName,
          lastName: message.senderUser?.lastName,
        })}
      </div>
      <div
        className={`min-w-0 max-w-[80%] rounded-lg border px-3 py-2 ${
          isOwn ? styles.bubbleOwn : 'border-gray-100 bg-gray-50'
        }`}
      >
        <div
          className={`flex items-baseline gap-2 flex-wrap ${isOwn ? 'flex-row-reverse' : ''}`}
        >
          <p className="text-xs font-semibold text-gray-800">
            {message.senderUser?.firstName} {message.senderUser?.lastName}
          </p>
          <p className="text-[10px] text-gray-400">
            {formatDate(message.createdAt, {
              format: 'monthDayTime',
              locale: language,
            })}
          </p>
          {message.editedAt && (
            <p className="text-[10px] italic text-gray-400">
              ({labels.edited})
            </p>
          )}
          {isOwn && (
            <div
              className={`flex items-center gap-1 ${isOwn ? 'mr-auto' : 'ml-auto'}`}
            >
              <Button
                size="xs"
                variant="ghost"
                onClick={() => editing.onStart(message)}
                className={`text-gray-300 transition-colors p-1! ${styles.editHover}`}
              >
                <Edit2Icon size={12} />
              </Button>
              <Button
                size="xs"
                variant="ghost"
                type="button"
                onClick={() => onDelete(message)}
                className="text-gray-300 hover:text-red-500 transition-colors p-1!"
              >
                <Trash2Icon size={12} />
              </Button>
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="mt-1 flex flex-col gap-1">
            <textarea
              rows={2}
              value={editing.text}
              onChange={(e) => editing.onTextChange(e.target.value)}
              className={`w-full resize-none rounded border px-2 py-1 text-sm outline-none focus:ring-1 ${styles.editBorder}`}
            />
            <div className="flex gap-1">
              <Button
                size="xs"
                onClick={() => editing.onSave(message, editing.text)}
              >
                {labels.save}
              </Button>
              <Button size="xs" variant="outline" onClick={editing.onCancel}>
                {labels.cancel}
              </Button>
            </div>
          </div>
        ) : (
          <p
            className={`mt-0.5 text-sm leading-relaxed text-gray-600 ${isOwn ? 'text-right' : ''}`}
          >
            {message.body}
          </p>
        )}

        {attachmentsSlot}
      </div>
    </div>
  );
}

export const TaskMessageBubble = memo(TaskMessageBubbleBase);
